<script setup></script>
<template>
  <main class="giftcards-terms">
    <h1>Wine Access Gift Card Terms and Conditions</h1>
    <p>
      These Wine Access Gift Card Terms and Conditions (“Terms and Conditions”)
      apply to closed loop prepaid cards (“Gift Cards”) in issued electronic
      form issued by Wine Access Card Fulfillment, LLC, a Nevada limited
      liability company (“Issuer”). These Terms and Conditions apply only to
      Gift Cards purchased by customers or provided in connection with a Gift
      Subscription, as defined below, and do not apply to any gift cards that
      may be provided by Issuer or any of its affiliates, including without
      charge or as a coupon or marketing promotion.
    </p>

    <p>
      <strong>Purchase of Gift Cards.</strong> Gift Cards are available for
      purchase only from Issuer and only online at
      <a href="https://www.wineaccess.com/giftcard/"
        >www.wineaccess.com/giftcard/</a
      >
      or by calling the Issuer at <CustomerServicePhoneLink />. Gift Cards
      purchased from sources other than Issuer are not valid and may have less
      redeemable balance or value than promised by an unauthorized seller.
      Neither Issuer nor Wine Access is responsible for honoring invalid gift
      cards or misrepresented or invalid balances or values.
    </p>

    <p>
      <strong>Gift Card Amount Limitations.</strong> Each single Gift Card may
      be purchased in any denomination between $10 and $2,000. Purchasers may
      not acquire multiple Gift Cards from Issuer in any given day in an
      aggregate amount that exceeds $10,000.
    </p>

    <p>
      <strong>Restrictions.</strong> Gift Cards cannot be used to purchase a
      WineAccess subscription as a gift to a third party (“Gift Subscription”)
      or other Gift Cards.
    </p>

    <p>
      <strong>Not Reloadable.</strong>
      Gift Cards cannot be reloaded, meaning funds may not be added to Gift
      Cards after activation. Also, the balance or value on a Gift Card cannot
      be used to purchase other Gift Cards.
    </p>

    <p>
      <strong>Delivery and Activation.</strong> Gift Cards are available for
      immediate delivery by email, and no shipping or processing fees apply to
      emailed Gift Cards. Gift Cards have no balance or value until activated at
      time of purchase, and Gift Cards will not be activated until payment is
      verified. Gift Cards will be activated when delivered to the designated
      recipient and will be active upon receipt by the recipient. Neither Issuer
      nor Wine Access is responsible for Gift Cards that are undeliverable or
      not received due to inaccurate delivery information, including e-mail
      addresses, spam filters on the recipient’s email, or otherwise.
    </p>

    <p>
      <strong>No Implied Warranties.</strong> Gift Cards are not credit, debit
      or charge cards, and have no implied warranties. TO THE FULL EXTENT
      PERMISSIBLE BY LAW, ISSUER AND WINE ACCES MAKE NO WARRANTIES, EXPRESS OR
      IMPLIED, WITH RESPECT TO GIFT CARDS OR YOUR BALANCE OR VALUE, INCLUDING
      WITHOUT LIMITATION, ANY EXPRESS OR IMPLIED WARRANTY OF MERCHANTABILITY OR
      FITNESS FOR A PARTICULAR PURPOSE. TO THE FULLEST EXTENT PERMISSIBLE BY
      LAW, IN THE EVENT A VALID GIFT CARD IS NON-FUNCTIONAL, YOUR SOLE REMEDY,
      AND OUR SOLE LIABILITY, WILL BE THE REPLACEMENT OF THAT GIFT CARD.
    </p>

    <p>
      <strong>Nonrefundable; not for resale.</strong> Once activated, Gift Cards
      may not be resold; resale or attempted resale is grounds for seizure and
      cancellation of the Gift Card without compensation. Gift Cards are not
      refundable or redeemable for cash, except where required by law.
    </p>

    <p>
      <strong>No Expiration Date.</strong> Gift Cards and the associated balance
      or value do not expire.
    </p>

    <p>
      <strong>Lost or Stolen Gift Card.</strong> The Issuer is not responsible
      for any lost or stolen Gift Cards or use without your permission. Maintain
      close control of the activation, redemption and/or access codes for your
      Gift Cards. Like cash, they are valuable and can be redeemed by whoever
      finds, accesses or obtains them. Lost, stolen or damaged Gift Cards will
      not be replaced.
    </p>

    <p>
      <strong>Not for Promotional Use.</strong> Gift Cards may be used as
      personal or business gifts but may not be used in connection with any
      marketing, advertising or other promotional activities, including without
      limitation via websites, internet advertisements, email, telemarketing,
      direct mail, newspaper and magazine advertisements, and radio and
      television broadcasts.
    </p>

    <p>
      <strong>Prohibition on Use of Wine Access Brand.</strong> Use of Wine
      Access’s name, logo, trade dress or trademarks in connection with the
      purchase or use of Gift Cards is prohibited. Further, the use of Gift
      Cards in any manner that states or implies that any person, website,
      business, product or service is endorsed or sponsored by, or otherwise
      affiliated with, Wine Access or any of its subsidiaries or affiliates is
      prohibited.
    </p>

    <p>
      <strong>Issuer.</strong> Gift Cards are issued by Issuer. Issuer reserves
      the right to, and holders or purchasers of a Gift Card agree that Issuer
      may, at any time change these Terms and Conditions, as permitted by law.
      Current versions of Terms and Conditions will be maintained and accessible
      at www.wineaccess.com/giftcard/terms/. Issuer may refuse, cancel or hold
      for review Gift Cards and Gift Card redemptions or orders in cases where
      it suspects fraud, mistake, alteration, issuance in an incorrect
      denomination, or violations of law, policies or these Terms and
      Conditions.
    </p>

    <p>
      <strong>Applicable Law.</strong> These Terms and Conditions and all
      matters arising out of or relating to the Gift Cards, except as governed
      by federal law, will be governed by the laws of the State of Nevada,
      without giving effect to any choice of law rule.
    </p>

    <div>
      <p>
        <strong>Redemption.</strong> Gift Cards are only redeemable online at
        <a href="https://www.wineaccess.com">www.wineaccess.com</a> and subject
        to the restrictions and limitations in these Terms and Conditions.
      </p>
      <p class="indent">
        <strong>a) Balance.</strong> The price of each transaction paid for by
        the Gift Card will be debited against the balance or value on the Gift
        Card. If the price for a transaction is greater than the balance or
        value on the Gift Card, the purchaser must pay the difference. If the
        price for a transaction is less than the balance or value on the Gift
        Card, the unused portion will remain on the Gift Card until it is
        redeemed and the balance or value of the Gift Card is decreased to zero,
        at which time the Gift Card will be deactivated and of no value.
      </p>

      <p class="indent">
        <strong>b) Refusal to Accept.</strong> Issuer and Wine Access may refuse
        to accept Gift Cards if the Gift Card number is invalid, the balance or
        value on the Gift Card is zero, the Gift Card has not been activated, or
        Gift Card cannot be found within the Issuer’s system.
      </p>
    </div>

    <p>
      <strong>Service Fee.</strong> After three years of inactivity, Gift Cards
      will incur a service fee of $1 per month. These fees will be deducted from
      the unused balance or value of the Gift Card until the remaining balance
      or value is redeemed for a purchase or the balance reaches zero, at which
      time the Gift Card will be deactivated and of no value.
    </p>

    <p>
      <strong>Checking the Balance.</strong> Holders or purchasers of a Gift
      Card may check the balance or value of the Gift Card by visiting
      www.wineaccess.com/accounts/credits/ or by calling
      <CustomerServicePhoneLink />. The balance or value you see when viewing
      Gift Card balances or values online or receive by telephone is an estimate
      only. In most cases, the balance or value is adjusted immediately when you
      make a purchase online, but there may be occasions when updated balance or
      value is delayed for a period of time.
    </p>

    <p>
      <strong>Disputes; Arbitration.</strong>
      Any dispute or claim relating in any way to Gift Cards or your balance or
      value will be resolved by binding arbitration, rather than in court,
      except that you may assert claims in small claims court if your claims
      qualify. The U.S. Federal Arbitration Act and U.S. federal arbitration law
      apply to this agreement. Arbitration does not have a judge or jury, and
      courts have only limited review of an arbitration award. However, an
      arbitrator can award on an individual basis the same damages and relief as
      a court (including injunctive and declaratory relief or statutory damages)
      and follows these Terms and Conditions as a court would. To begin an
      arbitration proceeding, you must send a letter requesting arbitration and
      describing your claim to: WineAccess, 236 8th St suite C, San Francisco,
      CA 94103 . Arbitration will be conducted by the American Arbitration
      Association ("AAA") under its rules, including the AAA's Supplementary
      Procedures for Consumer-Related Disputes. The AAA's rules are available at
      <a href="https://www.adr.org">www.adr.org</a>
      or by calling 1-800-778-7879. Payment of all filing, administration, and
      arbitrator fees will be governed by the AAA's rules. Issuer, Wine Access,
      and you agree that any dispute resolution proceedings will be conducted
      only on an individual basis and not in a class, consolidated, or
      representative action. If for any reason a claim proceeds in court rather
      than in arbitration, Issuer, Wine Access, and you waive any right to a
      jury trial. Notwithstanding the foregoing, Issuer, Wine Access, and you
      may bring suit in court to enjoin infringement or other misuse of
      intellectual property rights.
    </p>

    <p>
      <strong>Agreement; Assignment.</strong> These Terms and Conditions
      represent the complete and exclusive statement of agreement between you
      and the Issuer, and supersedes and merges all prior proposals and all
      other agreements governing your Gift Card. The Issuer may assign all or
      part of this agreement without notice to you, and then the Issuer would be
      released from all liability. You, however, may not assign this agreement
      to another party without the Issuer’s prior written consent and any
      attempted assignment will be void.
    </p>

    <p>
      <strong>Additional Information.</strong>
      You may contact Wine Access with questions or for help with their Gift
      Cards at <CustomerServiceEmailLink />. Agents are also available by phone
      <CustomerServicePhoneLink :with-hours-long="true" />.
    </p>
  </main>
</template>
